import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { authApi, setApiRole } from "../../api/api";
import { checkUserAuth, clearUserAuth, updateActivity, TOKEN_KEYS } from "../../utils/tokenUtils";
import styles from "../../styles/Login.module.css";

const Login = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("mobile"); // mobile | register | otp

  useEffect(() => {
    // Track user activity to update lastActivity
    const handleActivity = () => updateActivity();
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
    events.forEach(event => window.addEventListener(event, handleActivity));

    // Check authentication status using utility functions
    const checkAuth = async () => {
      const authResult = checkUserAuth();
      
      console.log('🔐 User Auth Check:', authResult);
      
      if (authResult.isAuthenticated) {
        console.log("✅ User token is valid, redirecting to home");
        navigate("/");
        return;
      }
      
      if (authResult.needsRefresh) {
        console.log("🔄 User token expired, attempting refresh...");
        try {
          const refreshToken = localStorage.getItem(TOKEN_KEYS.USER_REFRESH);
          const res = await authApi.post("/auth/refresh", { refreshToken });
          
          localStorage.setItem(TOKEN_KEYS.USER_ACCESS, res.data.accessToken);
          localStorage.setItem(TOKEN_KEYS.USER_REFRESH, res.data.refreshToken);
          updateActivity();
          
          console.log("✅ Token refreshed successfully, redirecting to home");
          navigate("/");
          
        } catch (error) {
          console.error("❌ Token refresh failed:", error);
          clearUserAuth();
        }
      } else {
        // Clear any invalid tokens
        console.log(`🧹 Clearing invalid user tokens: ${authResult.reason}`);
        clearUserAuth();
      }
    };

    checkAuth();

    // Cleanup listeners
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
    };
  }, [navigate]);
  
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isRegister, setIsRegister] = useState(false); // Track intent for registration
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const validateMobile = () => /^\d{10}$/.test(mobile);

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(30);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setResendDisabled(false);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOtp = async () => {
    if (resendDisabled) return;
    
    setError("");
    setResendDisabled(true);
    
    try {
      const otpRes = await authApi.post("/user/send-otp", { mobile });
      if (otpRes.data.status === "otp_sent") {
        startResendTimer();
      } else {
        setError(otpRes.data.message || "Failed to resend OTP");
        setResendDisabled(false);
      }
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
      setResendDisabled(false);
    }
  };

  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validateMobile()) {
      setError("Enter a valid 10-digit mobile number");
      setIsLoading(false);
      return;
    }

    try {
      const res = await authApi.post("/user/check-mobile", { mobile });

      if (res.data.status === "exists") {
        // Existing user, send OTP
        const otpRes = await authApi.post("/user/send-otp", { mobile });
        if (otpRes.data.status === "otp_sent") {
          setIsRegister(false); // login flow
          setStep("otp");
          startResendTimer(); // Start timer when OTP is first sent
        } else {
          setError(otpRes.data.message || "Failed to send OTP");
        }
      } else {
        // New user - collect name/email first
        setIsRegister(true);
        setStep("register");
      }
    } catch (err) {
      setError("Server error while checking mobile"+err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!name.trim()) {
      setError("Please enter your name");
      setIsLoading(false);
      return;
    }

    try {
      // Send OTP before registration
      const otpRes = await authApi.post("/user/send-otp", { mobile });
      if (otpRes.data.status === "otp_sent") {
        setTimeout(() => setStep("otp"), 0);
        startResendTimer(); // Start timer when OTP is first sent
      } else {
        setError(otpRes.data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError("Error sending OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Enhanced Client-side OTP Format Validation
    if (!otp || otp.length !== 6) {
      setError("OTP must be exactly 6 digits");
      setIsLoading(false);
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      setError("OTP must contain only numbers");
      setIsLoading(false);
      return;
    }

    // Check for obvious fake OTPs
    if (otp === "000000" || otp === "111111" || otp === "123456" || 
        otp === "999999" || otp === "888888" || otp === "777777") {
      setError("Please enter a valid OTP");
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        mobile,
        otp,
        isRegister,
      };

      if (isRegister) {
        payload.name = name;
        payload.email = email;
      }

      const res = await authApi.post("/user/verify-otp", payload);
      
      // Explicit Response Validation
      if (!res.data || res.data.status !== "success") {
        setError(res.data?.error || res.data?.message || "OTP verification failed");
        setIsLoading(false);
        return;
      }

      if (!res.data.accessToken) {
        setError("Authentication failed. Please try again.");
        setIsLoading(false);
        return;
      }

      // Successful verification - proceed with login
      localStorage.setItem(TOKEN_KEYS.USER_REFRESH, res.data.refreshToken);
      localStorage.setItem(TOKEN_KEYS.USER_ACCESS, res.data.accessToken);
      localStorage.setItem(TOKEN_KEYS.USER_INFO, JSON.stringify(res.data.user));
      updateActivity();
      setApiRole("user");
      const redirect = localStorage.getItem("postLoginRedirect");
      if (redirect) {
        localStorage.removeItem("postLoginRedirect");
        navigate(redirect);
      } else {
        navigate("/");
      }
    } catch (err) {
      // Enhanced Error Handling - Check multiple error indicators
      let errorMessage = "OTP verification failed";
      
      if (err.response?.status === 400) {
        // Bad request - likely OTP validation error
        if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.response?.data?.status === "failed") {
        // Explicit failure response
        errorMessage = err.response.data.error || err.response.data.message || "OTP verification failed";
      } else if (err.message) {
        // Client-side or network error
        if (err.message.toLowerCase().includes('otp') || 
            err.message.toLowerCase().includes('digit') || 
            err.message.toLowerCase().includes('valid')) {
          errorMessage = err.message;
        } else {
          errorMessage = "Network error. Please check your connection and try again.";
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError("");
    if (step === "register" || step === "otp") {
      setStep("mobile");
      setOtp("");
      setName("");
      setEmail("");
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case "mobile":
        return "Welcome Back";
      case "register":
        return "Create Account";
      case "otp":
        return "Verify OTP";
      default:
        return "Login";
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case "mobile":
        return "Enter your mobile number to continue";
      case "register":
        return "Complete your profile to get started";
      case "otp":
        return `OTP sent to +91 ${mobile}`;
      default:
        return "";
    }
  };

  return (
    <div className={styles.modernLoginContainer}>
      <div className={styles.modernLoginCard}>
        {/* Header */}
        <div className={styles.loginHeader}>
          <img
            src={require("../../assets/images/vedic-wisdom-logo.png")}
            alt="AstroShubh Logo"
            className={styles.loginLogoModern}
          />
          <div className={styles.loginProgress}>
            <div className={`${styles.progressStep} ${step === "mobile" ? styles.active : step !== "mobile" ? styles.completed : ""}`}>
              <div className={styles.stepNumber}>1</div>
            </div>
            <div className={styles.progressLine}></div>
            <div className={`${styles.progressStep} ${step === "register" ? styles.active : step === "otp" ? styles.completed : ""}`}>
              <div className={styles.stepNumber}>2</div>
            </div>
            <div className={styles.progressLine}></div>
            <div className={`${styles.progressStep} ${step === "otp" ? styles.active : ""}`}>
              <div className={styles.stepNumber}>3</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={styles.loginContent}>
          <div className={styles.loginTitleSection}>
            <h1 className={styles.loginTitleModern}>{getStepTitle()}</h1>
            <p className={styles.loginSubtitle}>{getStepSubtitle()}</p>
          </div>

          {/* Mobile Step */}
          {step === "mobile" && (
            <form onSubmit={handleMobileSubmit} className={styles.modernLoginForm}>
              <div className={styles.inputGroup}>
                <div className={styles.inputLabel}>Mobile Number</div>
                <div className={styles.inputWrapper}>
                  <span className={styles.countryCode}>+91</span>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 10-digit mobile number"
                    className={styles.modernInput}
                    maxLength={10}
                    disabled={isLoading}
                  />
                </div>
              </div>
              {error && (
                <div className={styles.modernError}>
                  <i className={styles.errorIcon}>⚠️</i>
                  {error}
                </div>
              )}
              <button type="submit" className={`${styles.modernBtn} ${styles.primary}`} disabled={isLoading || !validateMobile()}>
                {isLoading ? (
                  <span className={styles.btnLoading}>
                    <div className={styles.spinner}></div>
                    Processing...
                  </span>
                ) : (
                  <>
                    Continue
                    <i className={styles.btnIcon}>→</i>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Register Step */}
          {step === "register" && (
            <form onSubmit={handleRegisterSubmit} className={styles.modernLoginForm}>
              <div className={styles.inputGroup}>
                <div className={styles.inputLabel}>Full Name *</div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className={styles.modernInput}
                  disabled={isLoading}
                />
              </div>
              <div className={styles.inputGroup}>
                <div className={styles.inputLabel}>Email Address (Optional)</div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className={styles.modernInput}
                  disabled={isLoading}
                />
              </div>
              {error && (
                <div className={styles.modernError}>
                  <i className={styles.errorIcon}>⚠️</i>
                  {error}
                </div>
              )}
              <div className={styles.formActions}>
                <button type="button" onClick={handleBack} className={`${styles.modernBtn} ${styles.secondary}`} disabled={isLoading}>
                  <i className={styles.btnIcon}>←</i>
                  Back
                </button>
                <button type="submit" className={`${styles.modernBtn} ${styles.primary}`} disabled={isLoading || !name.trim()}>
                  {isLoading ? (
                    <span className={styles.btnLoading}>
                      <div className={styles.spinner}></div>
                      Sending OTP...
                    </span>
                  ) : (
                    <>
                      Send OTP
                      <i className={styles.btnIcon}>→</i>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* OTP Step */}
          {step === "otp" && (
            <form onSubmit={handleVerifyOtp} className={styles.modernLoginForm}>
              <div className={styles.inputGroup}>
                <div className={styles.inputLabel}>Enter OTP</div>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit OTP"
                  className={`${styles.modernInput} ${styles.otpInput}`}
                  maxLength={6}
                  disabled={isLoading}
                />
                <div className={styles.inputHint}>Check your SMS for the verification code</div>
              </div>
              {error && (
                <div className={styles.modernError}>
                  <i className={styles.errorIcon}>⚠️</i>
                  {error}
                </div>
              )}
              <div className={styles.formActions}>
                <button type="button" onClick={handleBack} className={`${styles.modernBtn} ${styles.secondary}`} disabled={isLoading}>
                  <i className={styles.btnIcon}>←</i>
                  Back
                </button>
                <button type="submit" className={`${styles.modernBtn} ${styles.primary}`} disabled={isLoading || otp.length !== 6}>
                  {isLoading ? (
                    <span className={styles.btnLoading}>
                      <div className={styles.spinner}></div>
                      Verifying...
                    </span>
                  ) : (
                    <>
                      Verify & Login
                      <i className={styles.btnIcon}>✓</i>
                    </>
                  )}
                </button>
              </div>
              <div className={styles.resendSection}>
                <span>Didn't receive OTP? </span>
                <button 
                  type="button" 
                  className={styles.resendBtn} 
                  onClick={handleResendOtp}
                  disabled={resendDisabled}
                >
                  {resendDisabled ? `Resend in ${resendTimer}s` : "Resend OTP"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className={styles.loginFooter}>
          <p>By continuing, you agree to our <a target="_blank" rel="noopener noreferrer" href="/terms-and-condition">Terms of Service</a> and <a target="_blank" rel="noopener noreferrer" href="/privacy-policy">Privacy Policy</a></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
